import { lazy, Suspense } from 'react'
import { createBrowserRouter, RouterProvider, Navigate, Outlet, useLocation, Link } from 'react-router-dom'
import { AppBar, Button, CircularProgress, Box, Toolbar, Typography } from '@mui/material'
import { useAuthStore } from '@store/authStore'
import { APP_NAME } from '@config/app.config'
import { ProtectedRoute } from '@components/auth/ProtectedRoute'
import { PublicOnlyRoute } from '@components/auth/PublicOnlyRoute'
import { MainLayout } from '@components/layout/MainLayout'
import { AuthLayout } from '@components/layout/AuthLayout'
import { RecorderProvider } from '@features/trail-recorder/context/RecorderProvider'
import { FloatingRecorderBar } from '@features/trail-recorder/components/FloatingRecorderBar'

// Wrap React.lazy so a chunk that fails to load — almost always after a deploy, where this
// tab references filenames the new build renamed, OR a just-deployed chunk that briefly 404s
// at an edge before it propagates — recovers by reloading to fetch the current build instead
// of surfacing "Failed to fetch dynamically imported module" to the user.
//
// A reload is only worth spending once the asset it would fetch actually exists, so this waits
// for that rather than guessing at it. It used to sleep a flat 800ms and reload regardless, three
// times — about 2.4 seconds in total, every one of those reloads blind to whether the chunk had
// arrived. A Cloudflare Pages deploy can take longer than that to reach an edge, and when it did
// the three reloads were spent before the file existed and the user got the error page anyway.
// (Observed twice during this project's own deploys, with chunk-reload-count sitting at its max.)
//
// The counter persists across the reloads (sessionStorage) and is cleared on any healthy load, so
// a genuinely-missing chunk still surfaces the error rather than looping forever.
const CHUNK_RELOAD_KEY = 'chunk-reload-count'
const CHUNK_RELOAD_MAX = 3

/**
 * How long to wait for the chunk to appear, indexed by how many reloads have already been spent.
 *
 * Escalating rather than flat, so an ordinary hiccup costs the user about two seconds and only a
 * genuinely slow propagation is waited out. The worst case is the sum — about twenty seconds
 * across all three attempts before the error boundary, against 2.4 seconds before.
 */
const CHUNK_WAIT_BUDGET_MS = [2_000, 6_000, 12_000]

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Wait until `url` serves JavaScript, or the budget runs out. Returns whether it arrived.
 *
 * Polled with `fetch`, not by retrying the import: a failed dynamic import is recorded in the
 * document's module map, so re-importing the same URL replays the first failure without touching
 * the network. fetch has no such memory, which makes it the only way to ask whether the asset has
 * turned up yet.
 *
 * HEAD, because the content type alone answers the question — Pages serves the SPA fallback as
 * text/html for a file it does not have, so "not HTML" means the real chunk is there. It also
 * keeps a poll that runs for twelve seconds from re-downloading the chunk on every pass.
 *
 * The interval doubles up to two seconds so a long wait costs a handful of requests, not dozens.
 */
async function waitForChunk(url: string, budgetMs: number): Promise<boolean> {
  const deadline = Date.now() + budgetMs
  let interval = 400

  while (Date.now() < deadline) {
    await sleep(Math.min(interval, Math.max(0, deadline - Date.now())))
    interval = Math.min(interval * 2, 2_000)

    const response = await fetch(url, { method: 'HEAD', cache: 'reload' }).catch(() => null)
    if (response?.ok && !/text\/html/i.test(response.headers.get('content-type') ?? '')) {
      return true
    }
  }

  return false
}

// A reload alone cannot fix one specific — and permanent — way this fails.
//
// Cloudflare Pages answers a request for an /assets/ file it does not have with the SPA
// fallback index.html, at status 200. Our own _headers rule then stamps that HTML with
// `public, max-age=31536000, immutable`, because the rule matches on request path and the
// path is under /assets/. So a browser that asks for a chunk during a deploy window — before
// that build is live at its edge — stores HTML against a .js URL for a year.
//
// From then on every import of that URL fails, in every tab, forever. Reloading does not
// help and never will: the entry is immutable, so it is served straight from cache and is
// never revalidated. The chunk is fine at the origin; only this browser's copy is wrong.
//
// `cache: 'reload'` is the way out. It bypasses the cache for the request *and* writes the
// response back over the bad entry, which is the only eviction script can perform. We walk
// the failed module's import graph, overwrite every entry whose cached copy is HTML, and
// then let the reload below run — this time against real JavaScript.
const CHUNK_SWEEP_LIMIT = 250
const CHUNK_SWEEP_DEPTH = 4

function failingChunkUrl(err: unknown): string | null {
  // Chrome and Firefox both put the URL in the message; Safari does not, and there we simply
  // fall through to the plain reload.
  const message = err instanceof Error ? err.message : String(err)
  return message.match(/https?:\/\/\S+?\.js/)?.[0] ?? null
}

async function body(url: string, cache: RequestCache): Promise<string> {
  try {
    return await (await fetch(url, { cache })).text()
  } catch {
    return ''
  }
}

// Returns how many poisoned entries were overwritten, so the caller can log it.
async function repairPoisonedChunks(err: unknown): Promise<number> {
  const start = failingChunkUrl(err)
  if (!start) return 0

  const seen = new Set<string>()
  let frontier = [start]
  let repaired = 0

  for (let depth = 0; depth < CHUNK_SWEEP_DEPTH && frontier.length; depth++) {
    const batch = frontier.filter((url) => !seen.has(url)).slice(0, CHUNK_SWEEP_LIMIT - seen.size)
    if (batch.length === 0) break
    batch.forEach((url) => seen.add(url))

    const next: string[] = []
    await Promise.all(
      batch.map(async (url) => {
        // force-cache reads what the cache holds without revalidating; with no entry it
        // simply fetches, so a cold cache cannot produce a false positive here.
        if (/^\s*<!doctype/i.test(await body(url, 'force-cache'))) {
          await fetch(url, { cache: 'reload' }).catch(() => {})
          repaired++
        }
        // Read the real module to find its dependencies — the failure surfaces on the module
        // the app asked for, but the poisoned entry is usually somewhere below it.
        const src = await body(url, 'no-store')
        for (const match of src.matchAll(/(?:from|import)\s*\(?\s*["'](\.\/[^"']+\.js)["']/g)) {
          next.push(new URL(match[1], url).href)
        }
      })
    )
    frontier = next
  }

  return repaired
}

function lazyWithRetry(factory: Parameters<typeof lazy>[0]) {
  return lazy(async () => {
    try {
      const mod = await factory()
      sessionStorage.removeItem(CHUNK_RELOAD_KEY)
      return mod
    } catch (err) {
      const attempts = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0)
      if (attempts >= CHUNK_RELOAD_MAX) {
        throw err
      }
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(attempts + 1))

      const budget = CHUNK_WAIT_BUDGET_MS[attempts] ?? CHUNK_WAIT_BUDGET_MS[CHUNK_WAIT_BUDGET_MS.length - 1]
      const url = failingChunkUrl(err)

      if (url) {
        // Don't spend the reload until the thing it would fetch is actually being served.
        const arrived = await waitForChunk(url, budget)
        if (!arrived) {
          console.warn(`[chunk] ${url} still not served after ${budget}ms, reloading anyway`)
        }
      } else {
        // Safari puts no URL in the message, so there is nothing to poll — wait out the same
        // budget blind, which is still far better than the flat 800ms this replaced.
        await sleep(budget)
      }

      // Repair before reloading. Retrying the import in-place would not work even now that
      // the cache is correct: this document's module map has already recorded the rejection
      // against that URL, so only a new document picks up the repaired bytes.
      const repaired = await repairPoisonedChunks(err).catch(() => 0)
      if (repaired > 0) {
        console.warn(`[chunk] replaced ${repaired} cached chunk(s) that held HTML, reloading`)
      }
      window.location.reload()
      // Hold the render until the reload takes effect so nothing flashes the error.
      return new Promise<never>(() => {})
    }
  })
}

// Pages are lazy-loaded so each route ships as its own chunk instead of being
// bundled into one giant entry file. This is the main lever for fast initial
// load: only the shell + the landing route download up front; everything else
// is fetched on navigation (and the chunks cache after the first visit).
// Adventure Trails hidden for now (route + nav entries disabled).
// const TrailsPage = lazyWithRetry(() => import('@pages/TrailsPage'))
const LeaderboardPage = lazyWithRetry(() => import('@pages/LeaderboardPage'))
const CollectionsPage = lazyWithRetry(() => import('@pages/CollectionsPage'))
const LoginPage = lazyWithRetry(() => import('@features/auth/pages/LoginPage'))
const RegisterPage = lazyWithRetry(() => import('@features/auth/pages/RegisterPage'))
const MfaVerifyPage = lazyWithRetry(() => import('@features/auth/pages/MfaVerifyPage'))
const ForgotPasswordPage = lazyWithRetry(() => import('@features/auth/pages/ForgotPasswordPage'))
const VerifyResetCodePage = lazyWithRetry(() => import('@features/auth/pages/VerifyResetCodePage'))
const ResetPasswordPage = lazyWithRetry(() => import('@features/auth/pages/ResetPasswordPage'))
const SearchResultsPage = lazyWithRetry(() => import('@features/search/pages/SearchResultsPage').then(m => ({ default: m.SearchResultsPage })))
const AdvancedSearchPage = lazyWithRetry(() => import('@features/search/pages/AdvancedSearchPage').then(m => ({ default: m.AdvancedSearchPage })))
const BuddiesPage = lazyWithRetry(() => import('@features/social/pages/BuddiesPage').then(m => ({ default: m.BuddiesPage })))
const GroupsPage = lazyWithRetry(() => import('@features/social/pages/GroupsPage').then(m => ({ default: m.GroupsPage })))
const SocialHubRedirect = lazyWithRetry(() => import('@features/social/pages/SocialHubRedirect').then(m => ({ default: m.SocialHubRedirect })))
const GroupDetailsPage = lazyWithRetry(() => import('@features/social/pages/GroupDetailsPage').then(m => ({ default: m.GroupDetailsPage })))
const ShareCodePage = lazyWithRetry(() => import('@features/social/pages/ShareCodePage').then(m => ({ default: m.ShareCodePage })))
const DiscussionLinkPage = lazyWithRetry(() => import('@features/social/pages/DiscussionLinkPage').then(m => ({ default: m.DiscussionLinkPage })))
const AdventureListPage = lazyWithRetry(() => import('@features/adventures/pages/AdventureListPage'))
const AdventureDetailPage = lazyWithRetry(() => import('@features/adventures/pages/AdventureDetailPage'))
const ScanLandingPage = lazyWithRetry(() => import('@features/adventures/pages/ScanLandingPage'))
const AdventureCreatePage = lazyWithRetry(() => import('@features/adventures/pages/AdventureCreatePage'))
const RouteAdventureWizardPage = lazyWithRetry(
  () => import('@features/adventures/pages/RouteAdventureWizardPage')
)
const AdventureEditPage = lazyWithRetry(() => import('@features/adventures/pages/AdventureEditPage'))
const MyAdventuresPage = lazyWithRetry(() => import('@features/adventures/pages/MyAdventuresPage'))
// Agreeing when a group goes. Deliberately outside the player: the vote happens days before
// anybody opens one, and the player is the part of this client with a known expiry.
const SessionPlanningPage = lazyWithRetry(() => import('@features/adventures/pages/SessionPlanningPage'))
const QRScannerPage = lazyWithRetry(() => import('@features/adventures/pages/QRScannerPage'))
const TrailsLibraryPage = lazyWithRetry(() => import('@features/trail-recorder/pages/TrailsLibraryPage'))
const TrailRecorderPage = lazyWithRetry(() => import('@features/trail-recorder/pages/TrailRecorderPage'))
const TrailReviewPage = lazyWithRetry(() => import('@features/trail-recorder/pages/TrailReviewPage'))
const TrailWalkPage = lazyWithRetry(() => import('@features/trail-recorder/pages/TrailWalkPage'))
const ProfilePage = lazyWithRetry(() => import('@features/profile/pages/ProfilePage'))
const SettingsPage = lazyWithRetry(() => import('@features/profile/pages/SettingsPage'))
const ChatPage = lazyWithRetry(() => import('@features/chat/pages/ChatPage'))
const NotificationsPage = lazyWithRetry(() => import('@features/notifications/pages/NotificationsPage'))
const SubscriptionPage = lazyWithRetry(() => import('@features/subscriptions/pages/SubscriptionPage'))
const MyRewardsPage = lazyWithRetry(() => import('@features/rewards/pages/MyRewardsPage'))
const RewardDetailPage = lazyWithRetry(() => import('@features/rewards/pages/RewardDetailPage'))
const GroupRewardTemplatesPage = lazyWithRetry(() => import('@features/rewards/pages/GroupRewardTemplatesPage'))
const GroupRewardTemplateEditPage = lazyWithRetry(() => import('@features/rewards/pages/GroupRewardTemplateEditPage'))
const GroupRewardTemplateStatsPage = lazyWithRetry(() => import('@features/rewards/pages/GroupRewardTemplateStatsPage'))
const MediaManagerPage = lazyWithRetry(() => import('@features/media/pages/MediaManagerPage'))
const CreateTicketPage = lazyWithRetry(() => import('@features/tickets/pages/CreateTicketPage'))
const MyTicketsPage = lazyWithRetry(() => import('@features/tickets/pages/MyTicketsPage'))
const SupportPage = lazyWithRetry(() => import('@features/support/pages/SupportPage'))
const TicketDetailPage = lazyWithRetry(() => import('@features/tickets/pages/TicketDetailPage'))
const ModeratorTicketDetailPage = lazyWithRetry(() => import('@features/tickets/pages/ModeratorTicketDetailPage'))
const CalendarEventListPage = lazyWithRetry(() => import('@features/calendarEvents/pages/CalendarEventListPage'))
const CalendarEventCreatePage = lazyWithRetry(() => import('@features/calendarEvents/pages/CalendarEventCreatePage'))
const MyEventsPage = lazyWithRetry(() => import('@features/calendarEvents/pages/MyEventsPage'))
const CalendarEventDetailPage = lazyWithRetry(() => import('@features/calendarEvents/pages/CalendarEventDetailPage'))
const CalendarEventEditPage = lazyWithRetry(() => import('@features/calendarEvents/pages/CalendarEventEditPage'))
const DashboardPage = lazyWithRetry(() => import('@features/dashboard/pages/DashboardPage'))
const CollectionEditorPage = lazyWithRetry(() => import('@features/adventures/pages/CollectionEditorPage'))
const CollectionCreateChooserPage = lazyWithRetry(() => import('@features/collections/pages/CollectionCreatePage'))
const CollectionCreatePage = lazyWithRetry(() => import('@features/collections/pages/CollectionCreatePage').then(m => ({ default: m.CollectionCreatePage })))
const CollectionDetailPage = lazyWithRetry(() => import('@features/adventures/pages/CollectionDetailPage'))
const StoriesPage = lazyWithRetry(() => import('@features/stories/pages/StoriesPage'))
const StoryPage = lazyWithRetry(() => import('@features/stories/pages/StoryPage'))
const StorySettingsPage = lazyWithRetry(() => import('@features/stories/pages/StorySettingsPage'))
const ModeratorApprovalsPage = lazyWithRetry(() => import('@features/adventures/pages/ModeratorApprovalsPage'))
const MyRunsPage = lazyWithRetry(() => import('@features/runStats/pages/MyRunsPage'))
const RunDetailPage = lazyWithRetry(() => import('@features/runStats/pages/RunDetailPage'))
const PublicRunPage = lazyWithRetry(() => import('@features/runStats/pages/PublicRunPage'))
const UnauthorizedPage = lazyWithRetry(() => import('@pages/UnauthorizedPage'))
const NotFoundPage = lazyWithRetry(() => import('@pages/NotFoundPage'))

// Content-area fallback shown while a route chunk loads. Sits INSIDE the layout
// so the nav shell stays on screen during in-app navigation.
function PageLoader() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" sx={{ flex: 1, minHeight: '60vh' }}>
      <CircularProgress />
    </Box>
  )
}

// Wraps a standalone (layout-less) lazy route in its own Suspense boundary.
function withSuspense(element: React.ReactNode) {
  return <Suspense fallback={<PageLoader />}>{element}</Suspense>
}

// Layout wrapper components to prevent remounting on navigation
function ProtectedMainLayout() {
  // Handle share-code redirect (/?{code}) BEFORE auth check so unauthenticated
  // users with share links land on /code/{code} instead of the login page.
  const location = useLocation()
  if (location.pathname === '/' && location.search && location.search.length > 1) {
    const code = location.search.substring(1)
    if (!code.includes('=')) {
      return <Navigate to={`/code/${encodeURIComponent(code)}`} replace />
    }
  }

  return (
    <ProtectedRoute>
      {/* RecorderProvider sits above the Outlet so an in-progress trail keeps
          recording across navigation; the FloatingRecorderBar follows along. */}
      <RecorderProvider>
        <MainLayout>
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </MainLayout>
        <FloatingRecorderBar />
      </RecorderProvider>
    </ProtectedRoute>
  )
}

// Pages anyone holding the link may open. Signed in, they sit in the normal app shell. Signed out,
// under a bare header: the app shell's hooks all assume a session, and a 401 without one sends the
// browser to /login — the opposite of what a shared link is for.
function PublicOrMainLayout() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  if (isAuthenticated) return <ProtectedMainLayout />
  return <SignedOutPublicLayout />
}

function SignedOutPublicLayout() {
  const location = useLocation()
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <AppBar position="static" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar sx={{ gap: 1 }}>
          <Typography
            component={Link}
            to="/login"
            variant="h6"
            noWrap
            sx={{ flex: 1, minWidth: 0, fontWeight: 700, color: 'inherit', textDecoration: 'none' }}
          >
            {APP_NAME}
          </Typography>
          <Button component={Link} to="/login" state={{ from: location }} variant="contained" size="small">
            Sign in
          </Button>
        </Toolbar>
      </AppBar>
      <Suspense fallback={<PageLoader />}>
        <Outlet />
      </Suspense>
    </Box>
  )
}

function PublicAuthLayout() {
  return (
    <PublicOnlyRoute>
      <AuthLayout>
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </AuthLayout>
    </PublicOnlyRoute>
  )
}

const router = createBrowserRouter([
  // Share code routes - public so they can handle auth redirects with referral codes
  {
    path: '/code/:shareCode',
    element: withSuspense(<ShareCodePage />),
  },
  {
    path: '/code',
    element: withSuspense(<ShareCodePage />),
  },
  // Public auth routes (login, register, etc.)
  {
    element: <PublicAuthLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/verify-reset-code', element: <VerifyResetCodePage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
    ],
  },
  // MFA verify (no PublicOnlyRoute - user might be partially authenticated)
  {
    path: '/mfa-verify',
    element: (
      <AuthLayout>
        {withSuspense(<MfaVerifyPage />)}
      </AuthLayout>
    ),
  },
  // QR Scanner (fullscreen, no MainLayout)
  {
    path: '/adventures/:id/scan',
    element: (
      <ProtectedRoute>
        {withSuspense(<QRScannerPage />)}
      </ProtectedRoute>
    ),
  },
  // Public, signed in or out (Backlog 048): a run whose runner chose to show its route and photos.
  {
    element: <PublicOrMainLayout />,
    children: [{ path: '/runs/:kind/:id', element: <PublicRunPage /> }],
  },
  // Main protected routes with MainLayout
  {
    element: <ProtectedMainLayout />,
    children: [
      { path: '/', element: <DashboardPage /> },
      // Adventure Trails hidden for now: { path: '/trails', element: <TrailsPage /> },
      { path: '/leaderboard', element: <LeaderboardPage /> },
      { path: '/collections', element: <CollectionsPage /> },
      // Create starts with the choice of type (Story or Achievements), fixed for good once made.
      { path: '/collections/create', element: <CollectionCreateChooserPage /> },
      { path: '/collections/create/story', element: <CollectionCreatePage kind="story" /> },
      { path: '/collections/create/achievements', element: <CollectionCreatePage kind="achievements" /> },
      // Detail is the public face of a collection; the editor moves under /edit. Search
      // results, profile activity and My Adventures all already linked to /collections/:id
      // expecting a viewer and were landing in the editor instead.
      { path: '/collections/:id', element: <CollectionDetailPage /> },
      { path: '/collections/:id/edit', element: <CollectionEditorPage /> },
      // Stories (Backlog 060): curated, ordered collections with their own page and reviews.
      { path: '/stories', element: <StoriesPage /> },
      { path: '/stories/:id', element: <StoryPage /> },
      { path: '/stories/:id/settings', element: <StorySettingsPage /> },
      // Buddies and Groups are destinations, not tabs of one hub — matching the phone's drawer.
      // The redirect runs the other way now: /social was the page and is a link people hold, so it
      // carries its old ?tab= across rather than dropping them somewhere they did not ask for.
      { path: '/buddies', element: <BuddiesPage /> },
      { path: '/groups', element: <GroupsPage /> },
      { path: '/social', element: <SocialHubRedirect /> },
      { path: '/trail-buddies', element: <Navigate to="/buddies" replace /> },
      { path: '/groups/:id', element: <GroupDetailsPage /> },
      // A discussion has no page of its own; this resolves one to its group / adventure / blog.
      { path: '/discussions/:discussionId', element: <DiscussionLinkPage /> },
      { path: '/groups/:id/rewards', element: <GroupRewardTemplatesPage /> },
      { path: '/groups/:id/rewards/new', element: <GroupRewardTemplateEditPage /> },
      { path: '/groups/:id/rewards/:templateId/edit', element: <GroupRewardTemplateEditPage /> },
      { path: '/groups/:id/rewards/:templateId/stats', element: <GroupRewardTemplateStatsPage /> },
      { path: '/media', element: <MediaManagerPage /> },
      { path: '/search', element: <SearchResultsPage /> },
      { path: '/search/advanced', element: <AdvancedSearchPage /> },
      { path: '/adventures', element: <AdventureListPage /> },
      { path: '/adventures/create', element: <AdventureCreatePage /> },
      { path: '/adventures/create/from-route', element: <RouteAdventureWizardPage /> },
      { path: '/adventures/:id', element: <AdventureDetailPage /> },
      // Where scan.hadv.quest sends a browser that the app did not catch.
      { path: '/scan/:shareCode/:nodeCode', element: <ScanLandingPage /> },
      { path: '/adventures/:id/edit', element: <AdventureEditPage /> },
      { path: '/moderator-approvals', element: <ModeratorApprovalsPage /> },
      { path: '/my-adventures', element: <MyAdventuresPage /> },
      { path: '/my-adventures/:tab', element: <MyAdventuresPage /> },
      // Run history (Backlog 047): per-run totals, 15-minute intervals and sharing.
      { path: '/my-runs', element: <MyRunsPage /> },
      { path: '/my-runs/:kind/:id', element: <RunDetailPage /> },
      { path: '/sessions/:sessionId/plan', element: <SessionPlanningPage /> },
      // Trail recorder (local-only GPX recording → adventure nodes)
      { path: '/trail-recorder', element: <TrailsLibraryPage /> },
      { path: '/trail-recorder/record', element: <TrailRecorderPage /> },
      { path: '/trail-recorder/:trailId', element: <TrailReviewPage /> },
      { path: '/trail-recorder/:trailId/walk', element: <TrailWalkPage /> },
      { path: '/events', element: <CalendarEventListPage /> },
      { path: '/events/create', element: <CalendarEventCreatePage /> },
      { path: '/events/my-events', element: <MyEventsPage /> },
      { path: '/events/:eventId', element: <CalendarEventDetailPage /> },
      { path: '/events/:eventId/edit', element: <CalendarEventEditPage /> },
      { path: '/profile', element: <ProfilePage /> },
      // Standalone profile editor removed; editing is inline on the profile page.
      { path: '/profile/edit', element: <Navigate to="/profile" replace /> },
      // :userId may also be a tab slug for own-profile deep links (e.g. /profile/recent);
      // ProfilePage disambiguates since real userIds are GUIDs.
      { path: '/profile/:userId', element: <ProfilePage /> },
      { path: '/profile/:userId/:tab', element: <ProfilePage /> },
      { path: '/settings', element: <SettingsPage /> },
      { path: '/settings/:section', element: <SettingsPage /> },
      { path: '/subscription', element: <SubscriptionPage /> },
      { path: '/rewards', element: <MyRewardsPage /> },
      { path: '/rewards/:instanceId', element: <RewardDetailPage /> },
      { path: '/chat', element: <ChatPage /> },
      { path: '/notifications', element: <NotificationsPage /> },
      // Support hub: Tickets | Suggestions | Moderation, tab in ?tab=. /support/tickets stays its own
      // page — the account menu's "Support" is just the tickets somebody sent, and a way to send one.
      // The old standalone lists redirect into the hub so held links and notices still land.
      { path: '/support', element: <SupportPage /> },
      { path: '/support/new', element: <CreateTicketPage /> },
      { path: '/contributions', element: <Navigate to="/support?tab=suggestions" replace /> },
      { path: '/support/tickets', element: <MyTicketsPage /> },
      { path: '/support/tickets/:ticketId', element: <TicketDetailPage /> },
      { path: '/tickets', element: <Navigate to="/support?tab=moderation" replace /> },
      { path: '/tickets/:ticketId', element: <ModeratorTicketDetailPage /> },
      { path: '/unauthorized', element: <UnauthorizedPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])

export function AppRouter() {
  return <RouterProvider router={router} />
}
